/**
 * The canonical form of a page URL, shared by everything that writes to `page`.
 *
 * Search Console and a sitemap have to agree character for character or the
 * reconciliation invents pages that are missing from an index they were never
 * in, so the rules here are deliberately conservative: only the parts of a URL
 * that provably cannot change which document is served are removed.
 *
 *   - scheme and host lowercased, default port dropped   (definitionally equal)
 *   - fragment dropped                                   (never sent to a server)
 *   - a bare trailing "?" dropped                        (an empty query is no query)
 *
 * Everything else survives, and the two temptations are both refused. Trailing
 * slashes stay: /blog and /blog/ are separate URLs to Google and can rank
 * separately. Query strings stay whole: ?page=2 is a different document, and no
 * allowlist of "tracking" parameters is right for every site.
 */
export function normalizePageUrl(input: string, base?: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  let url: URL
  try {
    url = new URL(trimmed, base)
  } catch {
    return null
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  if (!url.hostname) return null

  url.hash = ''
  // The URL parser already lowercases the host and drops :80 / :443.
  const text = url.toString()
  return text.endsWith('?') ? text.slice(0, -1) : text
}

/**
 * Whether a URL belongs to a site's inventory.
 *
 * Subdomains count, because a Search Console domain property reports them and
 * excluding them here would make the sitemap look short against data that
 * already includes them.
 */
export function urlBelongsToSite(url: string, host: string): boolean {
  let hostname: string
  try {
    hostname = new URL(url).hostname.toLowerCase()
  } catch {
    return false
  }

  const bare = hostname.replace(/^www\./, '')
  return bare === host || bare.endsWith(`.${host}`)
}

/** The origins to try, in order, when nothing is known but the domain. */
export function candidateOrigins(host: string): string[] {
  return [`https://${host}`, `https://www.${host}`, `http://${host}`]
}

/**
 * Tried only when robots.txt names no sitemap and Search Console has none
 * either. Ordered by how often each actually exists, because every miss is a
 * request to someone else's server.
 */
export const SITEMAP_PROBE_PATHS = [
  '/sitemap.xml',
  '/sitemap_index.xml',
  '/sitemap-index.xml',
  '/wp-sitemap.xml',
  '/sitemap.xml.gz',
  '/sitemaps.xml',
] as const
